import CardFilters from '../components/CardFilters';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import SearchBar from '../components/SearchBar';
import HotCards from '../components/HotCards';
import CardCandidateList from '../components/CardCandidateList';
import EmptySearchResult from '../components/EmptySearchResult';
import { useSearchStore } from '../stores/searchStore';
import type { ClassifyPrediction } from '../types';

const SEARCH_EXAMPLES = ['204/XY-P', '変装したピカチュウ', '变装皮卡丘', 'Poncho Pikachu'];

export default function HomePage() {
  const navigate = useNavigate();
  const {
    mode,
    searchQuery,
    setSearchQuery,
    searchByText,
    searchByImage,
    imagePreviewUrl,
    candidates,
    totalCount,
    isLoading,
    isLoadingMore,
    paginationError,
    error,
    clearSearch,
    loadMoreCandidates,
    hasMoreCandidates,
  } = useSearchStore();

  const handleCandidateSelect = (prediction: ClassifyPrediction) => {
    const card = prediction.card || {
      uid: prediction.id_label,
      name: prediction.id_label,
    };
    navigate(`/card/${prediction.id_label}`, { state: { card } });
  };

  const renderResults = () => {
    if (isLoading && mode !== 'idle') {
      return (
        <section className="rounded-[24px] border border-border bg-white px-6 py-16 text-center shadow-panel" aria-live="polite">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-2 border-border border-t-cyan" />
          <p className="eyebrow mt-6 text-cyan">Resolving card identity</p>
          <h2 className="mt-2 text-xl font-semibold tracking-[-0.03em] text-primary">
            {mode === 'image' ? 'Reading the card image…' : `Matching “${searchQuery}”…`}
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-secondary">Checking card artwork, printed numbers, names, and marketplace catalog mappings.</p>
        </section>
      );
    }

    if (error) {
      return (
        <section className="rounded-[24px] border border-red-200 bg-white px-6 py-14 text-center shadow-panel" role="alert">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">!</div>
          <h2 className="mt-4 text-xl font-semibold text-primary">We could not complete that search</h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-secondary">{error}</p>
          <button onClick={clearSearch} className="mt-6 rounded-xl bg-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate">Reset search</button>
        </section>
      );
    }

    if ((mode === 'image' || mode === 'text') && candidates.length > 0) {
      return (
        <CardCandidateList
          candidates={candidates}
          onSelect={handleCandidateSelect}
          onBack={clearSearch}
          onLoadMore={mode === 'text' ? loadMoreCandidates : undefined}
          hasMore={mode === 'text' ? hasMoreCandidates() : false}
          isLoadingMore={isLoadingMore}
          paginationError={paginationError}
          total={mode === 'text' ? totalCount : undefined}
        />
      );
    }

    if ((mode === 'image' || mode === 'text') && candidates.length === 0) {
      return (
        <EmptySearchResult
          type={mode}
          onBack={clearSearch}
          onImageUpload={mode === 'image' ? searchByImage : undefined}
        />
      );
    }

    return (
      <div className="space-y-12 sm:space-y-16">
        <section className="grid gap-4 md:grid-cols-3">
          {[
            ['01', 'Search anything you know', 'Use a card image, printed number, marketplace ID, or mixed Chinese, Japanese and English text.'],
            ['02', 'Confirm one exact printing', 'We resolve different market catalogs back to a shared Pokémon card identity before showing prices.'],
            ['03', 'Compare sold evidence', 'Review matched market activity and price history in one consistent USD view.'],
          ].map(([step, title, description]) => (
            <article key={step} className="rounded-[22px] border border-border bg-white p-5 shadow-sm">
              <span className="font-mono text-xs font-semibold text-cyan">{step}</span>
              <h2 className="mt-4 text-base font-bold tracking-[-0.02em] text-primary">{title}</h2>
              <p className="mt-2 text-sm leading-6 text-secondary">{description}</p>
            </article>
          ))}
        </section>

        <HotCards />
      </div>
    );
  };

  const isIdle = mode === 'idle';

  return (
    <Layout>
      <section className={`relative overflow-hidden rounded-[30px] bg-ink px-5 text-white shadow-[0_28px_80px_rgba(8,13,24,.20)] sm:px-8 ${isIdle ? 'pb-8 pt-9 sm:pb-10 sm:pt-12' : 'pb-6 pt-7'}`}>
        <div className="pointer-events-none absolute right-[-8rem] top-[-10rem] h-[26rem] w-[26rem] rounded-full bg-cyan/10 blur-[90px]" />
        <div className="pointer-events-none absolute bottom-[-8rem] left-[18%] h-[18rem] w-[18rem] rounded-full bg-amber/10 blur-[90px]" />
        <div className="relative">
          {isIdle ? (
            <div className="mb-8 grid gap-7 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
              <div>
                <p className="eyebrow text-cyan">Global Pokémon card search</p>
                <h1 className="mt-4 max-w-3xl text-4xl font-bold leading-[1.02] tracking-[-0.05em] sm:text-5xl lg:text-6xl">
                  Find the exact card.<br />Compare every market.
                </h1>
                <p className="mt-5 max-w-2xl text-sm leading-6 text-white/52 sm:text-base sm:leading-7">
                  Start with any language, any catalog ID, or a card image. AICardSearch resolves one Pokémon card identity before comparing its sold-price evidence.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 lg:max-w-[240px] lg:justify-end">
                {['中文', '日本語', 'English', 'Mixed input'].map((language) => (
                  <span key={language} className="rounded-full border border-white/12 bg-white/5 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/48">
                    {language}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="eyebrow text-cyan">Identity resolver</p>
                <h1 className="mt-2 text-2xl font-bold tracking-[-0.04em] sm:text-3xl">Confirm the exact Pokémon card</h1>
              </div>
              <button onClick={clearSearch} className="rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-white/60 hover:bg-white/10 hover:text-white">
                New search
              </button>
            </div>
          )}

          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            onSearch={searchByText}
            onImageUpload={searchByImage}
            imagePreviewUrl={imagePreviewUrl}
            onClearSearch={clearSearch}
            isLoading={isLoading && mode === 'text'}
            isImageLoading={isLoading && mode === 'image'}
          />
          <CardFilters />

          {isIdle && (
            <div className="mt-4 flex flex-wrap items-center gap-2 px-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-white/30">Try</span>
              {SEARCH_EXAMPLES.map((example) => (
                <button key={example} onClick={() => searchByText(example)} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 font-mono text-[10px] text-white/50 transition-colors hover:border-cyan/30 hover:text-cyan">
                  {example}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="mt-8 sm:mt-10">{renderResults()}</div>
    </Layout>
  );
}
